import React from 'react';
import { GlobeAltIcon } from '@heroicons/react/24/outline';
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogDescription,
    DialogTitle,
    Field,
    Input,
    InputError,
    Label,
    Spinner,
    Text
} from '../UI';
import { useForm } from '../../hooks';
import { IBrand, IChannel, IChannelType, IPermission, IUser, useBrands, useCapabilities, useLogin } from '../../services';
import { normalizeStorefrontDomain, validateStorefrontDomain } from '../../pages/settings/channels/utils/validate_storefront_domain';

const FORM_KEY = 'storefront_domain';

/** Staff accounts are not tied to a real storefront, so they never get the prompt. */
const EXEMPT_PERMISSIONS = ['TECHNICIAN', 'SUPERUSER', 'SUPERADMIN'];

// #region isShopifyConnected
const isShopifyConnected = (brand: IBrand | null): boolean => {
    return !!brand?.channels?.some((channel: IChannel) => channel.channel_type === IChannelType.SHOPIFY && channel.enabled);
};

// #region StorefrontDomainModal
/**
 * Non-dismissible backfill prompt.
 *
 * Self-gating: shows only for a brand that has an enabled Shopify channel but no
 * `storefront_domain` yet, and only for non-staff users. It cannot be closed by
 * Escape, backdrop click or an X — only by saving a valid domain.
 */
export const StorefrontDomainModal: React.FunctionComponent = (): React.ReactElement | null => {
    const [user] = useLogin();
    const [, loggedInBrand, singleBrand, , keyFunctions] = useBrands();

    const [open, setOpen] = React.useState(false);
    const [checked, setChecked] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [submitError, setSubmitError] = React.useState<string | null>(null);

    const form = useForm([{ key: FORM_KEY, validation: validateStorefrontDomain }]);

    const { hasShopify, resolved: capabilitiesResolved } = useCapabilities();

    const permission = (user?.permissions?.permission_id as IPermission | undefined)?.permission;
    const isExempt = !!permission && EXEMPT_PERMISSIONS.includes(permission);
    // A brand with no live Shopify connection has no storefront domain to backfill.
    const suppressedByCapability = capabilitiesResolved && !hasShopify;

    React.useEffect(() => {
        if (checked) return;
        // withAuth already hard-blocks unverified users with its own panel; do not stack on it.
        if (!user?.brand_id || !user.email_verified || isExempt) return;
        if (!capabilitiesResolved || !hasShopify) return;
        evaluateGate(user as IUser);
    }, [user, checked, isExempt, capabilitiesResolved, hasShopify]);

    // Close as soon as a domain lands on the brand from anywhere (wizard, settings).
    React.useEffect(() => {
        if (open && (loggedInBrand?.storefront_domain || singleBrand?.storefront_domain)) {
            setOpen(false);
        }
    }, [open, loggedInBrand?.storefront_domain, singleBrand?.storefront_domain]);

    // #region evaluateGate
    /**
     * `channels[]` only comes back from `GET /brands/:id`, and the Dashboard shell
     * does not fetch it, so resolve the brand here before deciding.
     */
    const evaluateGate = async (currentUser: IUser) => {
        const cached = singleBrand?.id === currentUser.brand_id ? singleBrand : null;
        const brand = cached ?? await keyFunctions.getBrand(currentUser.brand_id);

        setChecked(true);
        if (!brand) return;
        if (!isShopifyConnected(brand)) return;
        if (brand.storefront_domain) return;

        setOpen(true);
    };

    // #region handleSave
    const handleSave = async () => {
        if (!form.isValid || submitting) return;

        setSubmitting(true);
        setSubmitError(null);

        const normalized = normalizeStorefrontDomain(form.valueAtKey(FORM_KEY));
        const result = await keyFunctions.updateStorefrontDomain(normalized);

        setSubmitting(false);

        if (result) {
            setOpen(false);
        } else {
            setSubmitError('We could not save that domain. Please double-check it and try again.');
        }
    };

    if (suppressedByCapability) return null;
    if (!open) return null;

    return (
        <Dialog static={true} open={open} onClose={() => { /* blocking — only a successful save closes this */ }} size="lg">
            <DialogTitle>
                <div className="flex items-center gap-x-2">
                    <GlobeAltIcon className="h-6 w-6 text-emerald-500" />
                    <span>One more thing &mdash; what&apos;s your store&apos;s website?</span>
                </div>
            </DialogTitle>
            <DialogDescription>
                We use your public store domain to attribute analytics correctly and to build
                customer-facing links back to your store. Enter the domain your customers visit,
                not your <b>.myshopify.com</b> address.
            </DialogDescription>
            <DialogBody>
                <Field>
                    <Label>Store Website</Label>
                    <Input
                        name={FORM_KEY}
                        type="text"
                        autoComplete="off"
                        placeholder="yourstore.com"
                        value={form.valueAtKey(FORM_KEY)}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setValueAtKey(FORM_KEY, e.target.value)}
                    />
                    <InputError message={form.errors[FORM_KEY]} />
                    <p className="text-xs text-stone-500 mt-1">
                        No need for <b>https://</b> or <b>www.</b> &mdash; just the domain.
                    </p>
                </Field>
                {submitError && (
                    <Text className="!text-sm text-red-600 dark:text-red-400 mt-3">{submitError}</Text>
                )}
            </DialogBody>
            <DialogActions>
                <Spinner show={submitting} />
                <Button
                    color="emerald"
                    disabled={!form.isValid || submitting}
                    onClick={handleSave}
                >
                    {submitting ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
